// src/hooks/use-brand-config.ts
import { useState, useEffect, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import defaultConfig from "@/config/theme.default";
import { BrandConfig, BrandCustomization } from "@/types/brand";
import { useTheme } from "@/components/theme-provider";
import { generateDarkColorSet } from "@/utils/dark-color-generator";

// Helper function to merge user customizations with base brand config
const mergeUserCustomizations = (
  baseConfig: BrandConfig,
  userCustomization?: BrandCustomization,
): BrandConfig => {
  if (!baseConfig) return defaultConfig;
  if (!userCustomization) return baseConfig;

  const merged = { ...baseConfig };

  // Merge logo settings
  if (userCustomization.logo?.url) {
    merged.logo = {
      ...merged.logo,
      url: userCustomization.logo.url,
      altText: userCustomization.logo.altText || merged.logo?.altText || "",
      width: 30, // Fixed at 30px as specified
      height: 30, // Fixed at 30px as specified
    };
  }

  // Merge app name
  if (userCustomization.appName && userCustomization.appName.trim()) {
    merged.appName = userCustomization.appName.trim();
  }

  // Merge colors
  if (userCustomization.colors) {
    const userColors = userCustomization.colors;

    // Determine dark colors to use
    let darkColors = userCustomization.darkColors;
    if (!userCustomization.useSeparateDarkColors) {
      // Auto-generate dark colors if not using separate ones
      darkColors = generateDarkColorSet(userColors);
    }

    // Update light theme with user colors
    if (merged.themes && merged.themes.light) {
      if (userColors.primary) {
        merged.themes.light.primary = userColors.primary;
      }
      if (userColors.primaryForeground) {
        merged.themes.light.primaryForeground = userColors.primaryForeground;
      }
      if (userColors.secondary) {
        merged.themes.light.secondary = userColors.secondary;
      }
      if (userColors.secondaryForeground) {
        merged.themes.light.secondaryForeground =
          userColors.secondaryForeground;
      }
    }

    // Update dark theme with dark colors (either custom or generated)
    if (merged.themes && merged.themes.dark) {
      if (darkColors?.primary) {
        merged.themes.dark.primary = darkColors.primary;
      }
      if (darkColors?.primaryForeground) {
        merged.themes.dark.primaryForeground = darkColors.primaryForeground;
      }
      if (darkColors?.secondary) {
        merged.themes.dark.secondary = darkColors.secondary;
      }
      if (darkColors?.secondaryForeground) {
        merged.themes.dark.secondaryForeground = darkColors.secondaryForeground;
      }
    }

    // Update chart palette to prioritize user's colors
    if (
      (userColors.primary || userColors.secondary) &&
      merged.themes?.light?.chartPalette &&
      merged.themes?.dark?.chartPalette
    ) {
      const newLightChartPalette = [...merged.themes.light.chartPalette];
      const newDarkChartPalette = [...merged.themes.dark.chartPalette];

      // Replace first color with primary if available
      if (userColors.primary) {
        newLightChartPalette[0] = userColors.primary;
      }
      if (darkColors?.primary) {
        newDarkChartPalette[0] = darkColors.primary;
      }

      // Replace second color with secondary if available
      if (userColors.secondary) {
        newLightChartPalette[1] = userColors.secondary;
      }
      if (darkColors?.secondary) {
        newDarkChartPalette[1] = darkColors.secondary;
      }

      merged.themes.light.chartPalette = newLightChartPalette;
      merged.themes.dark.chartPalette = newDarkChartPalette;
    }
  }

  return merged;
};

// Helper function to convert HSL string to CSS custom property format
const hslToCssVar = (hslString: string): string => {
  // Convert "hsl(220, 84%, 55%)" to "220 84% 55%"
  const match = hslString.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (match) {
    return `${match[1]} ${match[2]}% ${match[3]}%`;
  }
  return hslString;
};

// Helper function to generate hover color from base color
const generateHoverColor = (hslString: string): string => {
  const match = hslString.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (!match) return hslString;

  const hue = parseInt(match[1]);
  const saturation = parseInt(match[2]);
  const lightness = parseInt(match[3]);

  // Generate hover color by adjusting lightness
  // For lighter colors (>50%), darken by 5-10%
  // For darker colors (<=50%), lighten by 5-10%
  const hoverLightness =
    lightness > 50
      ? Math.max(10, lightness - 8) // Darken light colors
      : Math.min(90, lightness + 8); // Lighten dark colors

  return `${hue} ${saturation}% ${hoverLightness}%`;
};

export const useBrandConfig = () => {
  const [brandConfig, setBrandConfig] = useState<BrandConfig>(defaultConfig);
  const [isInitialized, setIsInitialized] = useState(false);
  const { theme } = useTheme();
  const { user, isLoaded: userLoaded } = useUser();

  // Function to apply theme colors to CSS variables
  const applyThemeColors = useCallback(
    (config: BrandConfig) => {
      if (!config || !config.themes) return;

      // Skip DOM manipulation in test environment
      if (process.env.NODE_ENV === "test" || import.meta.env?.MODE === "test")
        return;

      const activeTheme =
        theme === "system"
          ? window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light"
          : theme;

      const themeColors = config.themes[activeTheme];
      if (!themeColors) return;

      const root = document.documentElement;

      // Determine if this is a custom brand configuration
      const userCustomization =
        userLoaded &&
        (user?.unsafeMetadata?.brandCustomization as
          | BrandCustomization
          | undefined);

      const hasCustomColors =
        userCustomization?.colors || userCustomization?.darkColors;

      // Set data-brand attribute
      if (hasCustomColors) {
        root.setAttribute("data-brand", "custom");
      } else {
        root.setAttribute("data-brand", "default");
      }

      // Apply comprehensive theme colors for custom brands
      if (hasCustomColors) {
        // Remove any existing custom brand styles
        const existingStyle = document.getElementById("custom-brand-styles");
        if (existingStyle) {
          existingStyle.remove();
        }

        // Create CSS for custom brand
        const customCSS = `
          html[data-brand="custom"]${activeTheme === "dark" ? ".dark" : ""} {
            --primary: ${hslToCssVar(themeColors.primary)};
            --primary-hover: ${generateHoverColor(themeColors.primary)};
            --primary-foreground: ${themeColors.primaryForeground ? hslToCssVar(themeColors.primaryForeground) : activeTheme === "dark" ? "210 20% 10%" : "210 20% 98%"};
            --secondary: ${hslToCssVar(themeColors.secondary)};
            --secondary-hover: ${generateHoverColor(themeColors.secondary)};
            --secondary-foreground: ${themeColors.secondaryForeground ? hslToCssVar(themeColors.secondaryForeground) : activeTheme === "dark" ? "210 20% 10%" : "210 20% 98%"};
            --card-header: ${hslToCssVar(themeColors.secondary)};
            --card-header-foreground: ${themeColors.secondaryForeground ? hslToCssVar(themeColors.secondaryForeground) : activeTheme === "dark" ? "210 20% 10%" : "210 20% 98%"};
            --ring: ${hslToCssVar(themeColors.primary)};
            --accent: ${hslToCssVar(themeColors.primary)};
            --accent-foreground: ${themeColors.primaryForeground ? hslToCssVar(themeColors.primaryForeground) : activeTheme === "dark" ? "210 20% 10%" : "210 20% 98%"};
          }
        `;

        // Inject the custom CSS
        const styleElement = document.createElement("style");
        styleElement.id = "custom-brand-styles";
        styleElement.textContent = customCSS;
        document.head.appendChild(styleElement);
      } else {
        // Remove custom brand styles for default brand
        const existingStyle = document.getElementById("custom-brand-styles");
        if (existingStyle) {
          existingStyle.remove();
        }
      }
    },
    [theme, user, userLoaded],
  );

  // Effect to merge user customizations with default config
  useEffect(() => {
    const updateBrandConfig = () => {
      // Get user customizations if available
      const userCustomization =
        userLoaded &&
        (user?.unsafeMetadata?.brandCustomization as
          | BrandCustomization
          | undefined);

      // Always use default config as base, merge user customizations
      const mergedConfig = mergeUserCustomizations(
        defaultConfig,
        userCustomization,
      );

      setBrandConfig(mergedConfig);

      // Apply the colors to CSS variables
      applyThemeColors(mergedConfig);
    };

    updateBrandConfig();
    setIsInitialized(true);
  }, [user, userLoaded, applyThemeColors]); // Re-run when user data changes

  // Effect to reapply colors when theme changes
  useEffect(() => {
    if (isInitialized) {
      applyThemeColors(brandConfig);
    }
  }, [theme, isInitialized, brandConfig, applyThemeColors]);

  const getChartPalette = useCallback(() => {
    if (!isInitialized || !brandConfig || !brandConfig.themes) return [];
    const activeTheme =
      theme === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : theme;

    return brandConfig?.themes?.[activeTheme]?.chartPalette || [];
  }, [brandConfig, theme, isInitialized]);

  return {
    brandConfig,
    isInitialized,
    getChartPalette,
    applyThemeColors: () => applyThemeColors(brandConfig),
  };
};
